<template>
  <el-dialog
    title="活动发卡"
    :visible.sync="dialogVisible"
    width="62%"
    :show-close="!dialogBusy"
    :before-close="handleBeforeClose"
    @close="handleClose"
    custom-class="card-grant-dialog"
    append-to-body
  >
    <!-- loading 要连提交一起盖住：只在预览时遮罩的话，提交途中还能改手机号，
         而回包结果会按手机号回写行状态，那次改动会被悄悄覆盖 -->
    <div v-loading="cardGrantPreviewLoading || cardGrantLoading" ref="cardGrantScroll" class="card-grant-scroll">
      <el-form label-width="100px">
        <el-form-item label="发卡门店" required>
          <!-- 门店由活动带出，只读展示 -->
          <el-input
            :value="storeName"
            disabled
            class="card-grant-readonly-field"
          ></el-input>
        </el-form-item>
        <el-form-item label="发卡活动" required>
          <!-- 活动是发卡的来源，只读展示 -->
          <el-input
            :value="activityTitle"
            disabled
            class="card-grant-readonly-field"
          ></el-input>
        </el-form-item>
      </el-form>

      <!-- 表单以下内容统一缩进 100px，与上方表单控件左边界对齐 -->
      <div class="card-grant-main">
      <!-- 录入工具条：间距由 gap 统一 -->
      <div class="card-grant-toolbar">
        <el-button size="small" type="primary" icon="el-icon-plus" :disabled="dialogBusy" @click="addCardGrantRow">逐条添加</el-button>
        <el-upload
          ref="cardGrantUpload"
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleCardGrantFileChange"
          :disabled="dialogBusy"
          accept=".xls,.xlsx"
          action="#"
        >
          <el-button size="small" type="primary" plain icon="el-icon-upload" :disabled="dialogBusy">导入Excel</el-button>
        </el-upload>
        <el-button
          size="small"
          type="text"
          icon="el-icon-download"
          @click="downloadTemplate"
        >下载模板</el-button>
        <el-button
          size="small"
          type="text"
          class="card-grant-btn-text-danger"
          icon="el-icon-delete"
          @click="clearCardGrantRows"
          :disabled="dialogBusy || (!cardGrantRows.length && !cardGrantResult)"
        >清空</el-button>
      </div>

      <el-alert
        v-if="cardGrantErrors.length"
        type="warning"
        :closable="false"
        show-icon
        class="card-grant-alert"
        :title="`有 ${cardGrantErrors.length} 条数据未通过校验，这些行没有导入，请修正后重新导入`"
      >
        <div class="card-grant-alert__list">
          <div v-for="(err, i) in cardGrantErrors" :key="i">{{ err }}</div>
        </div>
      </el-alert>

      <div class="card-grant-section-title">
        待发卡名单
        <span v-if="cardGrantRows.length" class="card-grant-section-count">{{ cardGrantRows.length }} 条</span>
      </div>
      <el-table
        ref="cardGrantTable"
        :data="cardGrantRows"
        size="small"
        border
        max-height="320"
        :row-class-name="cardGrantRowClass"
      >
        <div slot="empty" class="card-grant-empty">
          <i class="el-icon-tickets card-grant-empty__icon"></i>
          <div class="card-grant-empty__text">还没有待发卡数据</div>
          <div class="card-grant-empty__hint">
            点击「逐条添加」手动录入，或用「导入Excel」批量导入手机号
          </div>
          <el-button
            size="small"
            type="primary"
            plain
            icon="el-icon-plus"
            :disabled="dialogBusy"
            @click="addCardGrantRow"
          >逐条添加</el-button>
        </div>
        <el-table-column type="index" label="#" width="50" align="center"></el-table-column>
        <el-table-column label="手机号" width="170" align="center" class-name="card-grant-phone-col">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.phone"
              size="small"
              maxlength="11"
              placeholder="11位手机号"
              :disabled="isRowLocked(scope.row) || dialogBusy"
              @input="resetRowStatus(scope.row)"
            ></el-input>
          </template>
        </el-table-column>
        <el-table-column label="时长(天)" width="110" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.days"
              size="small"
              :min="1"
              :max="3650"
              controls="false"
              :disabled="isRowLocked(scope.row) || dialogBusy"
              style="width: 100%;"
            ></el-input-number>
          </template>
        </el-table-column>
        <el-table-column label="发放原因" min-width="160">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.reason"
              size="small"
              maxlength="200"
              :disabled="isRowLocked(scope.row) || dialogBusy"
            ></el-input>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.status === 'success'" type="success" size="mini">已发放</el-tag>
            <el-tag v-else-if="scope.row.status === 'duplicate'" type="warning" size="mini">重复跳过</el-tag>
            <el-tag v-else-if="scope.row.status === 'failed'" type="danger" size="mini">发放失败</el-tag>
            <el-tag v-else type="info" size="mini">待发放</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="70" align="center">
          <template slot-scope="scope">
            <el-button
              type="text"
              style="color: #f56c6c;"
              :disabled="isRowLocked(scope.row) || dialogBusy"
              @click="removeCardGrantRow(scope.$index)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="cardGrantRows.length && !hasPendingRows && !cardGrantResult" class="card-grant-result__hint">
        <div>当前名单全部为重复跳过，无需再次发卡</div>
        <div>关闭后可在「活动发卡记录」里查看完整台账</div>
      </div>

      <div v-if="cardGrantResult" ref="cardGrantResult" class="card-grant-result">
        <div class="card-grant-result__title">
          发卡结果
          <span class="card-grant-section-count">提交 {{ cardGrantResult.total }} 条</span>
        </div>
        <div
          v-for="group in cardGrantResultGroups"
          :key="group.key"
          class="card-grant-result__group"
        >
          <span :style="{ color: group.color }" class="card-grant-result__label">{{ group.label }} {{ group.phones.length }}：</span>
          <span class="card-grant-result__phones">
            <el-tag
              v-for="(phone, i) in group.phones"
              :key="i"
              :type="group.tagType"
              size="mini"
              effect="plain"
              class="card-grant-result__phone-tag"
            >{{ phone }}</el-tag>
          </span>
        </div>
        <div
          v-if="cardGrantResult.failedDetails && cardGrantResult.failedDetails.length"
          class="card-grant-result__errors"
        >
          <div v-for="(item, i) in cardGrantResult.failedDetails" :key="i">
            失败原因：{{ item.phone || '（未知手机号）' }} — {{ item.reason }}
          </div>
        </div>
        <!-- 全部处理完时给一句"接下来能做什么"：此时弹窗已经停在结果上，
             不说明白用户只能盯着「完成」猜自己是不是必须关掉。
             继续发下一批的入口在上方工具条（导入Excel 会整批替换、逐条添加是追加），
             所以不另设按钮，避免和工具条上的「清空」重复 -->
        <div v-if="resultSettled" class="card-grant-result__hint">
          <div>本批已完成。要继续发下一批，直接用上方「导入Excel」或「逐条添加」</div>
          <div>关闭后可在「活动发卡记录」里查看完整台账</div>
        </div>
      </div>
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <!-- 出结果之后这次操作已经生效，「取消」就不成立了；但也不自动关闭——
           失败的行还要就地改手机号重发，关掉就等于把重试入口丢了 -->
      <el-button v-if="!resultSettled" :disabled="dialogBusy" @click="handleClose">{{ issuedAny ? '关 闭' : '取 消' }}</el-button>
      <!-- 全部处理完（成功 / 重复跳过）：主按钮切成「完成」并直接关闭，
           不留在那让用户再点一次「发卡」然后吃一个"没有待发放的数据"的报错 -->
      <el-button
        v-if="resultSettled"
        type="primary"
        icon="el-icon-check"
        :disabled="dialogBusy"
        @click="handleClose"
      >完 成</el-button>
      <el-button
        v-else
        type="danger"
        icon="el-icon-s-promotion"
        @click="submitCardGrant"
        :loading="cardGrantLoading"
        :disabled="!hasPendingRows || dialogBusy"
      >发 卡</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { parseCardGrantTime } from '../../utils/cardGrantTime'

export default {
  name: 'GrantDialog',

  props: {
    // 弹窗显隐，父组件用 v-model 或 :visible.sync 绑定
    visible: {
      type: Boolean,
      default: false
    },
    // 发卡门店，由活动带出
    storeId: {
      type: Number,
      required: true
    },
    // 门店名称（只读展示）
    storeName: {
      type: String,
      default: ''
    },
    // 来源活动
    activityId: {
      type: Number,
      required: true
    },
    // 活动标题（只读展示）
    activityTitle: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      dialogVisible: this.visible,
      cardGrantLoading: false,
      cardGrantPreviewLoading: false,
      cardGrantForm: {
        storeId: this.storeId,
        source: 1 // 1=逐条添加 2=Excel导入
      },
      cardGrantRows: [], // [{ phone, days, reason, status: pending|success|duplicate|failed, remark }]
      cardGrantErrors: [], // Excel 预览时未通过校验的错误信息
      cardGrantResult: null, // 发卡结果 { total, successPhones, duplicatePhones, failedPhones, failedDetails }
      issuedAny: false
    };
  },

  computed: {
    // 门店发卡结果的三类名单分组（计数由数组长度得出，不另设字段；空名单的组不渲染）
    cardGrantResultGroups() {
      const result = this.cardGrantResult;
      if (!result) return [];
      return [
        { key: "success", label: "成功", color: "#67c23a", tagType: "success", phones: result.successPhones || [] },
        { key: "duplicate", label: "重复跳过", color: "#e6a23c", tagType: "warning", phones: result.duplicatePhones || [] },
        { key: "failed", label: "失败", color: "#f56c6c", tagType: "danger", phones: result.failedPhones || [] }
      ].filter((group) => group.phones.length);
    },
    // 还有没有需要接着处理的行：待发放、发放失败都算（失败行改手机号即可重试）。
    // 已发放、重复跳过的行不会再产生新结果，不计入
    hasPendingRows() {
      return this.cardGrantRows.some(
        (row) => row.status === "pending" || row.status === "failed"
      );
    },
    // 本次操作是否已经收口：有结果、且没有待处理行。用它切换底部按钮的文案与行为
    resultSettled() {
      return !!this.cardGrantResult && !this.hasPendingRows;
    },
    // 预览/提交请求未结束时禁止关闭，避免请求回包后才发现卡已经发出
    dialogBusy() {
      return this.cardGrantLoading || this.cardGrantPreviewLoading;
    }
  },

  watch: {
    visible(val) {
      this.dialogVisible = val;
    },
    // 活动换了门店时同步，弹窗内始终以当前活动为准
    storeId(val) {
      this.cardGrantForm.storeId = val;
    }
  },

  methods: {
    // 预览/提交期间拦截 X、遮罩、ESC 的关闭动作；底部取消按钮另由 dialogBusy 禁用
    handleBeforeClose(done) {
      if (this.dialogBusy) return;
      done();
    },
    // 关闭对话框：清空全部临时状态
    handleClose() {
      if (this.dialogBusy) return;
      this.dialogVisible = false;
      this.$emit('update:visible', false);
      this.cardGrantForm.source = 1;
      this.cardGrantRows = [];
      this.cardGrantErrors = [];
      this.cardGrantResult = null;
      this.issuedAny = false;
      if (this.$refs.cardGrantUpload) {
        this.$refs.cardGrantUpload.clearFiles();
      }
    },
    // 已发放/重复跳过的行不再允许改手机号、时长、原因，避免页面显示与台账不一致
    isRowLocked(row) {
      return !!row && (row.status === "success" || row.status === "duplicate");
    },
    // 逐条添加一行
    addCardGrantRow() {
      // 出过结果之后再添加，说明是在发起下一批：上一批的结果块留着，
      // 会和下面新的待发放行混在一起（「提交 5 条」对着一张 6 行的表）。
      // 导入 Excel 本来就会清空结果，这里对齐同一个语义
      this.cardGrantResult = null;
      this.cardGrantRows.push({
        phone: "",
        days: 1,
        reason: "",
        status: "pending",
        remark: "",
        source: 1
      });
      // 条数超过 max-height(320px) 之后新行落在折叠线以下，不滚过去等于点了没反应
      this.scrollToLastRow();
    },
    // 名单表格的滚动容器（无固定列时只有这一个 body-wrapper）
    tableBody() {
      const table = this.$refs.cardGrantTable;
      return table ? table.$el.querySelector(".el-table__body-wrapper") : null;
    },
    // 把视口带到最后一行：新加的行总在末尾，滚到底就是它
    scrollToLastRow() {
      this.$nextTick(() => {
        const body = this.tableBody();
        if (!body) return;
        body.scrollTop = body.scrollHeight;
      });
    },
    // 修改手机号后该行回到待发放（状态跟手机号绑定，改号即可重发）
    resetRowStatus(row) {
      if (row && row.status && row.status !== "pending") {
        row.status = "pending";
      }
    },
    // 删除一行
    removeCardGrantRow(index) {
      this.cardGrantRows.splice(index, 1);
    },
    // 清空全部行（发卡结果、校验提示一并清空，来源复位为逐条添加）
    clearCardGrantRows() {
      this.cardGrantRows = [];
      this.cardGrantErrors = [];
      this.cardGrantResult = null;
      this.cardGrantForm.source = 1;
    },
    // 行背景按状态区分：重复跳过浅黄、发放失败浅红
    cardGrantRowClass({ row }) {
      if (!row) return "";
      if (row.status === "duplicate") return "card-grant-duplicate-row";
      if (row.status === "failed") return "card-grant-failed-row";
      return "";
    },
    // 下载模板（内容与批量发卡同款，仅文件名区分）
    async downloadTemplate() {
      try {
        const response = await this.$http.get('template/download', {
          params: {
            templateName: 'batch_gift_card'
          },
          responseType: 'blob'
        });
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = '活动发卡模版.xlsx';
        link.click();
        window.URL.revokeObjectURL(link.href);
        this.$message.success('模板下载成功');
      } catch (error) {
        console.error('模板下载失败：', error);
        this.$message.error('模板下载失败');
      }
    },
    // 选择 Excel：走预览接口解析，结果回填成可编辑表格
    async handleCardGrantFileChange(file) {
      if (this.dialogBusy) {
        if (this.$refs.cardGrantUpload) this.$refs.cardGrantUpload.clearFiles();
        return;
      }
      const raw = file && file.raw;
      if (!raw) return;

      this.cardGrantPreviewLoading = true;
      try {
        const formData = new FormData();
        formData.append("file", raw);
        // storeId 必须传：预览接口靠它查重，标出「该门店已发放过」的行
        formData.append("storeId", this.cardGrantForm.storeId);

        const { data: res } = await this.$http.post("cardgrant/preview", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        if (res.status === 0 && res.data) {
          const preview = res.data;
          // 导入是「整批替换」，不是追加：直接覆盖当前名单。
          // 先把新名单算出来，再决定换不换
          const incoming = (preview.rows || []).map((row) => ({
            phone: row.phone || "",
            days: row.days || 1,
            reason: row.reason || "",
            status: row.duplicated ? "duplicate" : "pending",
            remark: row.remark || "",
            source: 2
          }));
          // 错误清单先渲染，下面提前返回时用户能看到为什么没导进来
          this.cardGrantErrors = preview.errors || [];

          // 一条有效行都没解析出来时不动原名单：换成空数组等于把用户已经录好的数据删了，
          // 而他要的新数据又没进来。真要清空，工具条上有「清空」
          if (!incoming.length) {
            const tail = preview.errorCount ? `，${preview.errorCount} 条未通过校验` : "";
            this.$message.warning(`没有解析到可发放的数据，当前名单未变${tail}`);
            return;
          }

          const replaced = this.cardGrantRows.length;
          this.cardGrantRows = incoming;
          this.cardGrantForm.source = 2;
          this.cardGrantResult = null;
          // 整批换过之后滚动位置还停在上一批的位置，拉回顶部看新名单第一条
          this.$nextTick(() => {
            const body = this.tableBody();
            if (body) body.scrollTop = 0;
          });

          let msg = `解析完成：可发放 ${preview.validCount} 条`;
          if (preview.duplicateCount) msg += `，重复 ${preview.duplicateCount} 条`;
          if (preview.errorCount) msg += `，未通过校验 ${preview.errorCount} 条`;
          // 覆盖式导入要说出来：不说用户会以为是在原有名单上追加
          if (replaced) msg += `；已替换原有 ${replaced} 条`;
          if (!preview.validCount) {
            this.$message.warning(msg);
          } else {
            this.$message.success(msg);
          }
        } else {
          this.$message.error(res.message || "Excel 解析失败");
        }
      } catch (error) {
        console.error("Excel 预览失败：", error);
        this.$message.error("Excel 解析失败：" + (error.message || "未知错误"));
      } finally {
        this.cardGrantPreviewLoading = false;
        if (this.$refs.cardGrantUpload) {
          this.$refs.cardGrantUpload.clearFiles();
        }
      }
    },
    // 提交前重新取活动：活动状态/有效期可能已被其他管理员修改
    async fetchCurrentActivity() {
      const { data: res } = await this.$http.get(`cardgrant/activity/${this.activityId}`);
      if (res.status !== 0 || !res.data) {
        throw new Error(res.message || "活动信息查询失败，请刷新后重试");
      }
      return res.data;
    },
    // 与后端 /cardgrant/issue 的校验口径一致：启用 + 当前时间落在有效期内 + 门店未停业
    getActivityDisabledReason(activity) {
      if (!activity || Number(activity.status) !== 1) return "该活动已停用，无法发卡";
      const start = parseCardGrantTime(activity.startTime);
      const end = parseCardGrantTime(activity.endTime);
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return "该活动有效期不完整，无法发卡";
      }
      const now = Date.now();
      if (now < start) return "该活动尚未开始，无法发卡";
      if (now > end) return "该活动已结束，无法发卡";
      if (activity.storeOpen === false) return "该门店已停业或不存在，无法发卡";
      return "";
    },
    // 提交发卡
    async submitCardGrant() {
      if (this.dialogBusy) return;
      // 已发放、重复跳过的行都不再提交，避免下一批把已发过的号码再送一遍
      const rows = this.cardGrantRows.filter(
        (row) =>
          row.status !== "success" &&
          row.status !== "duplicate" &&
          ((row.phone && row.phone.trim()) || (row.reason && row.reason.trim()))
      );
      if (!rows.length) {
        return this.$message.error(
          this.cardGrantRows.length
            ? "没有待发放的数据；已发放或重复行不会再次提交"
            : "请先添加待发放数据"
        );
      }
      // 逐行校验，规则与后端一致；同一批内不允许重复手机号，
      // 否则回包只能按手机号回写状态，同一号码的两行会被写成同一种状态
      const seenPhones = {};
      for (let i = 0; i < rows.length; i++) {
        const phone = (rows[i].phone || "").trim();
        if (!/^1[3-9]\d{9}$/.test(phone)) {
          return this.$message.error(`第 ${i + 1} 行手机号格式不正确`);
        }
        if (seenPhones[phone] !== undefined) {
          return this.$message.error(
            `第 ${i + 1} 行手机号与第 ${seenPhones[phone] + 1} 行重复，请修改后再提交`
          );
        }
        seenPhones[phone] = i;
        if (!rows[i].days || rows[i].days <= 0) {
          return this.$message.error(`第 ${i + 1} 行时长必须大于 0`);
        }
        if (!rows[i].reason || !rows[i].reason.trim()) {
          return this.$message.error(`第 ${i + 1} 行发放原因不能为空`);
        }
      }

      this.cardGrantLoading = true;
      try {
        // 活动可能在弹窗打开期间被改期/停用：提交前以服务端最新数据为准，
        // 避免前端拿着旧状态放行，最后只吃一个后端错误
        const activity = await this.fetchCurrentActivity();
        this.$emit('activity-updated', activity);
        const disabledReason = this.getActivityDisabledReason(activity);
        if (disabledReason) {
          this.$message.warning(disabledReason);
          return;
        }

        const payload = {
          storeId: this.cardGrantForm.storeId,
          activityId: this.activityId,
          cardType: "time",
          source: this.cardGrantForm.source,
          rows: rows.map((row) => ({
            phone: (row.phone || "").trim(),
            days: row.days,
            reason: (row.reason || "").trim(),
            source: row.source === 2 ? 2 : 1
          }))
        };

        const { data: res } = await this.$http.post("cardgrant/issue", payload);

        if (res.status === 0) {
          this.cardGrantResult = {
            total: res.total || 0,
            successPhones: res.successPhones || [],
            duplicatePhones: res.duplicatePhones || [],
            failedPhones: res.failedPhones || [],
            failedDetails: res.failedDetails || []
          };
          if (this.cardGrantResult.successPhones.length || this.cardGrantResult.duplicatePhones.length) {
            this.issuedAny = true;
          }

          // 按接口返回的三类名单把行状态落位：已发放 / 重复跳过 / 发放失败
          const statusByPhone = {};
          this.cardGrantResult.successPhones.forEach((p) => (statusByPhone[p] = "success"));
          this.cardGrantResult.duplicatePhones.forEach((p) => (statusByPhone[p] = "duplicate"));
          this.cardGrantResult.failedPhones.forEach((p) => (statusByPhone[p] = "failed"));
          this.cardGrantRows.forEach((row) => {
            const st = row.phone && statusByPhone[row.phone.trim()];
            if (st) row.status = st;
          });

          const parts = [`成功 ${this.cardGrantResult.successPhones.length}`];
          if (this.cardGrantResult.duplicatePhones.length) {
            parts.push(`重复跳过 ${this.cardGrantResult.duplicatePhones.length}`);
          }
          if (this.cardGrantResult.failedPhones.length) {
            parts.push(`失败 ${this.cardGrantResult.failedPhones.length}`);
            this.$message.warning(`发卡完成：${parts.join("，")}`);
          } else {
            this.$message.success(`发卡完成：${parts.join("，")}`);
          }

          this.$emit('issued', this.cardGrantResult);
          // 结果区在滚动容器最底部，名单长时一屏放不下：不主动滚过去，用户只看到一条 toast，
          // 失败明细藏在下面等于没给。等 DOM 落地后再滚
          this.$nextTick(this.scrollToResult);
        } else {
          this.$message.error(res.message || "发卡失败");
        }
      } catch (error) {
        console.error("活动发卡失败：", error);
        this.$message.error("发卡失败：" + (error.message || "未知错误"));
      } finally {
        this.cardGrantLoading = false;
      }
    },
    // 把结果区滚到可视范围内。自己算 scrollTop 而不用 scrollIntoView：
    // 后者会连带滚动整个页面，弹窗后面的列表也会跟着跳
    scrollToResult() {
      const wrap = this.$refs.cardGrantScroll;
      const box = this.$refs.cardGrantResult;
      if (!wrap || !box) return;
      // 用 getBoundingClientRect 求相对位置，不依赖 offsetParent 链
      const offset = box.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
      wrap.scrollTop = Math.max(0, wrap.scrollTop + offset - 12);
    }
  }
};
</script>

<style scoped>
/* ===== 活动发卡弹窗 ===== */
.card-grant-readonly-field {
  /* 弹窗较宽，字段限宽避免被拉到满屏 */
  width: 380px;
}
.card-grant-main {
  /* 表单以下内容统一缩进，与 el-form(label-width:100px) 的控件左边界对齐 */
  margin-left: 100px;
}
.card-grant-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}
.card-grant-toolbar >>> .el-button {
  /* 覆盖 Element 默认的 margin-left:10px */
  margin-left: 0 !important;
}
.card-grant-btn-text-danger:not(.is-disabled) {
  color: #f56c6c;
}
.card-grant-btn-text-danger:not(.is-disabled):hover {
  color: #f78989;
}
.card-grant-scroll {
  /* 名单较长时内容区内部滚动，底部「发卡」按钮始终可见 */
  max-height: 64vh;
  overflow-y: auto;
  padding-right: 6px;
}
.card-grant-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.card-grant-alert {
  margin-bottom: 10px;
}
.card-grant-alert__list {
  max-height: 120px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.6;
}
.card-grant-section-count {
  font-size: 12px;
  font-weight: 400;
  color: #909399;
}
/* 表格内输入框：去掉常驻边框，聚焦时才显示，整表更清爽 */
.card-grant-main >>> .el-table .el-input__inner {
  border-color: transparent;
  background-color: transparent;
}
.card-grant-main >>> .el-table .el-input__inner:hover {
  border-color: #dcdfe6;
}
.card-grant-main >>> .el-table .el-input__inner:focus {
  border-color: #409eff;
  background-color: #fff;
}
/* 手机号列：等宽字体 + 居中，便于逐位核对 */
.card-grant-main >>> .card-grant-phone-col .el-input__inner {
  font-family: Consolas, Monaco, "Courier New", monospace;
  text-align: center;
  letter-spacing: 0.5px;
}
.card-grant-empty {
  padding: 24px 0 8px;
}
.card-grant-empty__icon {
  font-size: 28px;
  color: #c0c4cc;
}
.card-grant-empty__text {
  margin-top: 8px;
  font-size: 13px;
  color: #909399;
}
.card-grant-empty__hint {
  margin: 4px 0 12px;
  font-size: 12px;
  color: #909399;
}
.card-grant-result {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
.card-grant-result__title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}
.card-grant-result__group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 1.6;
}
.card-grant-result__label {
  font-weight: 600;
  flex-shrink: 0;
}
.card-grant-result__phones {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
.card-grant-result__phone-tag {
  margin: 0;
}
.card-grant-result__errors {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.9;
  color: #f56c6c;
}
/* 收口提示：结果区里唯一的行动指引，压成辅助文字，别和结果数据抢注意力 */
.card-grant-result__hint {
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.6;
  color: #909399;
}
</style>

<!-- 全局样式，不使用scoped，确保可以影响el-table内部元素 -->
<style>
/* 门店发卡行背景按状态区分：重复跳过浅黄、发放失败浅红（已发放/待发放不着色） */
.el-table .card-grant-duplicate-row {
  background-color: #fdf6ec !important;
}

.el-table .card-grant-duplicate-row:hover > td {
  background-color: #faecd8 !important;
}

.el-table .card-grant-failed-row {
  background-color: #ffebeb !important;
}

.el-table .card-grant-failed-row:hover > td {
  background-color: #ffe0e0 !important;
}

/* 活动发卡弹窗：与批量发卡同款红色标题头，正文区更紧凑（内容含表格） */
.card-grant-dialog .el-dialog__header {
  background-color: #f56c6c;
  padding: 15px 20px;
}

.card-grant-dialog .el-dialog__title {
  color: white;
  font-weight: bold;
}

.card-grant-dialog .el-dialog__headerbtn .el-dialog__close {
  color: white;
  font-size: 20px;
}

.card-grant-dialog .el-dialog__body {
  padding: 20px 20px 10px;
}
</style>
